import { useState } from 'react';
import { useAdminList } from './useAdminList';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { AdminUser } from '../../../domain/Admin';

interface ConfirmState {
  header: string;
  message: string;
  onConfirm: () => void;
}

export function useAdminUsers() {
  const { admin } = useRepositories();
  const list = useAdminList((params) => admin.fetchUsers(params).then((r) => ({ items: r.users, total: r.total })));
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const runAction = async (id: string, action: () => Promise<unknown>) => {
    setBusyId(id);
    setActionError(null);
    try {
      await action();
      await list.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'No se pudo completar la acción.');
    } finally {
      setBusyId(null);
    }
  };

  const toggleAdmin = (u: AdminUser) => {
    if (u.isAdmin) {
      runAction(u.id, () => admin.updateUser(u.id, { isAdmin: false }));
      return;
    }
    setConfirm({
      header: 'Dar admin',
      message: `${u.name} va a poder entrar al backoffice y ver/editar todo. ¿Confirmás?`,
      onConfirm: () => runAction(u.id, () => admin.updateUser(u.id, { isAdmin: true })),
    });
  };

  const toggleDisabled = (u: AdminUser) => {
    if (u.isDisabled) {
      runAction(u.id, () => admin.updateUser(u.id, { isDisabled: false }));
      return;
    }
    setConfirm({
      header: 'Deshabilitar cuenta',
      message: `${u.name} no va a poder loguearse ni seguir usando la app hasta que lo vuelvas a habilitar. ¿Confirmás?`,
      onConfirm: () => runAction(u.id, () => admin.updateUser(u.id, { isDisabled: true })),
    });
  };

  const sendReset = (u: AdminUser) => {
    setConfirm({
      header: 'Mandar reset de contraseña',
      message: `Le va a llegar un mail a ${u.email} para elegir una contraseña nueva. ¿Confirmás?`,
      onConfirm: () =>
        runAction(u.id, async () => {
          await admin.sendPasswordReset(u.id);
          setToast('Mail de reset enviado.');
        }),
    });
  };

  return {
    users: list.items,
    filtered: list.items,
    total: list.total,
    loadingMore: list.loadingMore,
    loadMore: list.loadMore,
    query: list.query,
    setQuery: list.setQuery,
    loading: list.loading,
    error: actionError ?? list.error,
    busyId,
    confirm,
    setConfirm,
    toast,
    toggleAdmin,
    toggleDisabled,
    sendReset,
  };
}
